import { useCallback, useMemo, useState } from 'react';
import {
  DrawerForm,
  DrawerPanel,
  ErrorResponse,
  handleFieldErrors,
  ServerErrorFields,
} from '@dmc/ui';
import { VdsButton, VdsNotification } from '@volt/volt-extended-components-react';
import { useTranslation } from 'react-i18next';
import Field from '@cas/components/inputs/field/Field';
import CasFormInput from '@cas/components/inputs/cas-form-input/CasFormInput';
import { ChangePasswordModel, useChangePasswordMutation } from '@casops/store/apis/authApi';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

type FormModel = ChangePasswordModel & {
  confirmPassword: string;
};

const defaultValues: FormModel = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

const serverErrorFields: ServerErrorFields<FormModel> = {
  currentPassword: 'CurrentPassword',
  newPassword: 'NewPassword',
};

export function UserChangePasswordPanel() {
  const { t } = useTranslation();
  const [ChangePassword, { isLoading }] = useChangePasswordMutation();
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [successMessage, setSuccessMessage] = useState<string | undefined>(undefined);

  const schema = useMemo(() => {
    return z
      .object({
        currentPassword: z.string().min(1, { message: t('EnterAPassword') }),
        newPassword: z.string().min(1, { message: t('EnterAPassword') }),
        confirmPassword: z.string().min(1, { message: t('EnterAPassword') }),
      })
      .refine(data => data.newPassword === data.confirmPassword, {
        message: t('Validation.PasswordsDoNotMatch'),
        path: ['confirmPassword'], // This will show the error on the confirmPassword field
      });
  }, [t]);

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
    setError,
    reset,
  } = useForm<FormModel>({
    defaultValues,
    resolver: zodResolver(schema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  const onSubmit = useCallback(
    async (data: FormModel) => {
      setErrorMessage(undefined);
      setSuccessMessage(undefined);
      const { confirmPassword, ...submitData } = data;

      await ChangePassword(submitData)
        .unwrap()
        .then(() => {
          setSuccessMessage(t('PasswordChangedSuccessfully'));
          reset(defaultValues);
        })
        .catch(error => {
          const errorResponse = new ErrorResponse(error);
          const validationError = errorResponse.getValidationError();
          if (validationError) {
            handleFieldErrors<FormModel>({
              serverErrorFields,
              validationError,
              setError,
            });
          }

          setErrorMessage(errorResponse.getTitle());
        });
    },
    [ChangePassword, reset, setError, t],
  );

  const canSubmit = !isSubmitting && !isLoading;

  return (
    <DrawerPanel
      header={t('ChangePassword')}
      footer={
        <VdsButton
          variant="primary"
          size="medium"
          disabled={!canSubmit}
          onClick={handleSubmit(onSubmit)}
          isFullWidth
        >
          {t('ApplyChange')}
        </VdsButton>
      }
    >
      <DrawerForm>
        <>
          {successMessage && (
            <VdsNotification variant="inline" status="healthy" showCloseButton={false}>
              <span slot="body">{successMessage}</span>
            </VdsNotification>
          )}
          {errorMessage && (
            <VdsNotification variant="inline" status="danger" showCloseButton={false}>
              <span slot="body">{errorMessage}</span>
            </VdsNotification>
          )}
          <Field uid="currentPassword" label={t('CurrentPassword')}>
            <CasFormInput
              control={control}
              id="currentPassword"
              name="currentPassword"
              type="password"
              autocomplete="current-password"
              disabled={isLoading}
              size="medium"
            />
          </Field>
          <Field uid="newPassword" label={t('NewPassword')}>
            <CasFormInput
              control={control}
              id="newPassword"
              name="newPassword"
              type="password"
              autocomplete="new-password"
              disabled={isLoading}
              size="medium"
            />
          </Field>
          <Field uid="confirmPassword" label={t('ConfirmNewPassword')}>
            <CasFormInput
              control={control}
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              autocomplete="new-password"
              disabled={isLoading}
              size="medium"
            />
          </Field>
        </>
      </DrawerForm>
    </DrawerPanel>
  );
}

export default UserChangePasswordPanel;
